import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Briefcase } from 'lucide-react';
import { convertPrice } from '@utils/helpers';
import { PRICING_PLANS } from '@utils/constants';

const Pricing = ({ currency, setCurrency, openBooking }) => {
  return (
    <section id="pricing" className="py-24 bg-slate-900 text-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div 
          className="absolute inset-0" 
          style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }} 
        />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <h2 className="text-4xl font-bold mb-4">Simple, Transparent Pricing</h2>
          <p className="text-xl text-slate-300">
            Choose the package that fits your ambitions. All plans include CV optimization.
          </p>

          <div className="flex justify-center gap-4 mt-8">
            <button
              onClick={() => setCurrency('NGN')}
              className={`px-6 py-2 rounded-full font-medium transition-all ${
                currency === 'NGN' ? 'bg-white text-slate-900' : 'bg-slate-800 text-slate-300'
              }`}
            >
              Naira (₦)
            </button>
            <button
              onClick={() => setCurrency('USD')}
              className={`px-6 py-2 rounded-full font-medium transition-all ${
                currency === 'USD' ? 'bg-white text-slate-900' : 'bg-slate-800 text-slate-300'
              }`}
            >
              USD ($)
            </button>
          </div>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {PRICING_PLANS.map((plan, idx) => (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1 }}
              className={`relative rounded-3xl p-8 ${
                plan.popular 
                  ? 'bg-gradient-to-b from-indigo-600 to-violet-600 scale-105 shadow-2xl shadow-indigo-600/25' 
                  : 'bg-slate-800 border border-slate-700'
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-amber-400 text-slate-900 px-4 py-1 rounded-full text-sm font-bold">
                  Most Popular
                </div>
              )}

              <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
              <div className="mb-6">
                <span className="text-4xl font-bold">{convertPrice(plan.price, currency)}</span>
                <span className="text-slate-400">/month</span>
              </div>

              <div className="flex items-center gap-2 mb-6 text-sm">
                <Briefcase className="w-4 h-4 text-indigo-400" />
                <span>{plan.totalApps} applications total</span>
              </div>

              <ul className="space-y-4 mb-4">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-300 text-sm">{feature}</span>
                  </li>
                ))}
              </ul>

              <p className="text-xs text-slate-400 mb-6 italic">{plan.paymentNote}</p>

              <button
                onClick={() => openBooking(plan.id)}
                className={`w-full py-4 rounded-xl font-bold transition-all ${
                  plan.popular 
                    ? 'bg-white text-indigo-600 hover:bg-slate-100' 
                    : 'bg-slate-700 text-white hover:bg-slate-600'
                }`}
              >
                Get Started
              </button>
            </motion.div>
          ))}
        </div>

        <div className="mt-12 text-center text-slate-400 text-sm">
          <p>Sessions via Google Meet · Mon–Thu & Sat · No refunds after 24h or once work begins.</p>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
